import * as React from 'react';

import { cn } from '@/lib/utils';

interface TableProps extends React.ComponentProps<'table'> {
  noWrapper?: boolean;
}

const Table = ({ className, noWrapper, ...props }: TableProps) => {
  const table = (
    <table
      data-slot="table"
      className={cn(
        noWrapper ? '' : 'w-full',
        'caption-bottom text-xxs border-separate border-spacing-0',
        className,
      )}
      {...props}
    />
  );

  if (noWrapper) {
    return table;
  }

  return (
    <div
      data-slot="table-container"
      className="relative w-full overflow-x-auto"
    >
      {table}
    </div>
  );
};

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return (
    <thead
      data-slot="table-header"
      className={cn('[&_tr]:border-b', className)}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        'bg-muted/50 border-t font-medium [&>tr]:last:border-b-0',
        className,
      )}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'odd:bg-transparent even:bg-muted data-[state=selected]:bg-muted border-b border-warm-gray-100',
        className,
      )}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'bg-muted h-10 px-2 text-left align-middle font-bold whitespace-nowrap text-xxs leading-[18.164px] [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className,
      )}
      style={{
        fontFamily: 'var(--family-Primaire, "Open Sauce One")',
        color: 'var(--warm-gray-600)',
      }}
      {...props}
    />
  );
}

function TableCell({
  className,
  children,
  ...props
}: React.ComponentProps<'td'>) {
  const displayValue =
    children === null || children === undefined || children === ''
      ? '-'
      : children;

  return (
    <td
      data-slot="table-cell"
      className={cn(
        'p-2 align-middle whitespace-nowrap text-xxs [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className,
      )}
      {...props}
    >
      {displayValue}
    </td>
  );
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('text-muted-foreground mt-4 text-xxs', className)}
      {...props}
    />
  );
}

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
};
